import {createContext, use, useEffect, useRef, type ReactNode} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import {createCodeownersSlice, type CodeownersSlice} from './slices/codeowners'
import {
  createComparisonInfoSlice,
  type ComparisonInfoSlice,
  type ComparisonInfoSliceOpts,
} from './slices/comparison-info'
import {createDiffFilterSlice, type DiffFilterSlice} from './slices/diff-filter'
import {createDiffSummariesSlice, type DiffSummariesSlice, type DiffSummariesSliceOpts} from './slices/diff-summaries'
import {createMarkersSlice, type MarkersSlice, type MarkersSliceOpts} from './slices/markers'
import {
  createProgressiveDiffsSlice,
  type ProgressiveDiffSliceOpts,
  type ProgressiveDiffsSlice,
} from './slices/progressive-diffs'
import {getSelectedFullDiffHash, parsePathDigestWithoutLineNumbers} from '@github-ui/diffs/document-hash-helpers'
import {
  useUserDiffViewSettingsData,
  useWhitespacePreference,
  useSplitViewPreference,
} from '@github-ui/diff-view-settings/page-data/payloads/diff-view-settings'
import {
  createFilesCollapsedStatusSlice,
  type FilesCollapsedStatusSlice,
  type FilesCollapsedStatusSliceOpts,
} from './slices/files-collapsed-status'
import {
  buildCollapsedDiffStatusMap,
  buildDiffEntryMap,
  buildDiffSummaryMap,
  buildNormalizedMarkers,
  buildPendingReview,
} from '../helpers/diff-helpers'
import type {DiffEntry} from '@github-ui/diff-lines'
import {createDiffEntriesSlice, type DiffEntriesSlice, type DiffEntriesSliceOpts} from './slices/diff-entries'
import {diffEntriesKey} from '../page-data/loaders/use-diff-entries'
import {getQueryClient} from '@github-ui/react-core/query-client'
import {createPendingReviewSlice, type PendingReviewSlice, type PendingReviewSliceOpts} from './slices/pending-review'
import {
  createMarkerAndDiffNavigationSlice,
  type MarkerAndDiffNavigationOpts,
  type MarkerAndDiffNavigationSlice,
} from './slices/marker-and-diff-navigation'
import {createSettingsSlice, type SettingsSlice, type SettingsSliceOpts} from './slices/settings'
import {createPullRequestSlice, type PullRequestSlice, type PullRequestSliceOpts} from './slices/pull-request'
import {
  createSuggestionBatchSlice,
  type SuggestionBatchSlice,
  type SuggestionBatchSliceOpts,
} from './slices/suggestion-batch'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {
  createChangeGroupGenerationSlice,
  type ChangeGroupGenerationSlice,
  type ChangeGroupGenerationSliceOpts,
} from './slices/change-group-generation'
import {loadActiveBatchFromStorage} from '../helpers/suggestion-batch-storage'
import {createRepositorySlice, type RepositorySlice, type RepositorySliceOpts} from './slices/repository'
import {createCurrentUserSlice, type CurrentUserSlice, type CurrentUserSliceOpts} from './slices/current-user'
import {
  createConfigOptsSlice,
  type ConfigOpts,
  type ConfigOptsSlice,
  type ConfigOptsSliceOpts,
} from './slices/config-opts'
import {createViewSettingsSlice, type ViewSettingsSlice, type ViewSettingsSliceOpts} from './slices/view-settings'

type InitialState = ChangeGroupGenerationSliceOpts &
  ComparisonInfoSliceOpts &
  ConfigOptsSliceOpts &
  CurrentUserSliceOpts &
  DiffEntriesSliceOpts &
  DiffSummariesSliceOpts &
  FilesCollapsedStatusSliceOpts &
  MarkerAndDiffNavigationOpts &
  MarkersSliceOpts &
  SettingsSliceOpts &
  PendingReviewSliceOpts &
  ProgressiveDiffSliceOpts &
  PullRequestSliceOpts &
  RepositorySliceOpts &
  SuggestionBatchSliceOpts &
  ViewSettingsSliceOpts
export type State = ChangeGroupGenerationSlice &
  CodeownersSlice &
  ComparisonInfoSlice &
  ConfigOptsSlice &
  CurrentUserSlice &
  DiffEntriesSlice &
  DiffFilterSlice &
  DiffSummariesSlice &
  FilesCollapsedStatusSlice &
  MarkerAndDiffNavigationSlice &
  MarkersSlice &
  SettingsSlice &
  PullRequestSlice &
  RepositorySlice &
  SuggestionBatchSlice &
  PendingReviewSlice &
  ProgressiveDiffsSlice &
  ViewSettingsSlice

type PullRequestStore = ReturnType<typeof createPullRequestStore>

const PullRequestStoreContext = createContext<PullRequestStore | null>(null)

export function usePullRequestStore<T>(selector: (state: State) => T): T {
  const store = use(PullRequestStoreContext)
  if (!store) throw new Error('Missing PullRequestStoreProvider in the tree')
  return useStore(store, selector)
}

const defaultConfigOpts: ConfigOpts = {
  forceUnifiedView: false,
  useChangeGroups: false,
  useInlineComments: true,
  useEditOptions: true,
  useFileTree: false,
  useAdvancedToolbar: false,
  useBasicToolbar: true,
  userCanComment: true,
  treeCollapsedByDefault: false,
  treeEnableInlineCompactMode: false,
}

type PullRequestStoreProviderProps = {
  children?: ReactNode
  initData: FilesRoutePayload
  configOpts?: ConfigOpts
}
export const PullRequestStoreProvider = ({
  initData,
  configOpts = defaultConfigOpts,
  children,
}: PullRequestStoreProviderProps) => {
  const storeRef = useRef<PullRequestStore | null>(null)
  const hideWhitespace = useWhitespacePreference(initData.user.viewSettings.hideWhitespace)
  const userSplitPreference = useSplitViewPreference(initData.user.viewSettings.splitPreference)
  const commentsPreference = initData.user.viewSettings.commentsPreference

  // Update viewSettings in the store when they change (e.g., when user toggles split/unified)
  const {data: viewSettings} = useUserDiffViewSettingsData({
    splitPreference: userSplitPreference,
    lineSpacing: initData.user.viewSettings.lineSpacing,
    commentsPreference,
  })

  useEffect(() => {
    if (storeRef.current && viewSettings) {
      storeRef.current.getState().viewSettingsActions.setViewSettings(viewSettings)
    }
  }, [viewSettings])

  const diffSummaryMap = buildDiffSummaryMap(initData.diffSummaries)
  // this is also used as the initial state for all viewed files
  const collapsedFilesMap = buildCollapsedDiffStatusMap(initData.diffSummaries)
  const pendingReview = buildPendingReview(initData.viewerPendingReview)
  const initialDiffEntryMap = buildDiffEntryMap(initData.diffContents as DiffEntry[])
  const normalizedMarkers = buildNormalizedMarkers(initData.markers, initData.diffSummaries)
  const loadedBatch = loadActiveBatchFromStorage(initData.pullRequest.pathName, initData.pullRequest.comparison.headOid)

  // eslint-disable-next-line react-hooks/refs
  if (!storeRef.current) {
    populateTanstackDiffEntryCache(
      initData.comparison.selectedRange?.baseOid,
      initData.comparison.selectedRange?.headOid,
      initData.pullRequest.pathName,
      initData.diffContents as DiffEntry[],
      initData.pullRequest.comparison,
      hideWhitespace,
    )

    storeRef.current = createPullRequestStore({
      baseOid: initData.pullRequest.comparison.baseOid,
      headOid: initData.pullRequest.comparison.headOid,
      selectedCommitRange: initData.comparison.selectedRange,
      comparisonView: initData.comparison.viewing,
      changeGroupGeneration: initData.changeGroupGeneration,
      currentUser: initData.user,
      hideWhitespace,
      isSingleFileMode: initData.isSingleFileMode,
      collapsedFilesMap,
      initialSummariesViewedStatus: collapsedFilesMap,
      initialSummariesViewedCount: initData.user.viewedFilesCount,
      diffEntries: initialDiffEntryMap,
      commentsPreference,
      initialDiffSummaries: diffSummaryMap,
      initialLoadedPathDigests: new Set(initData.diffContents.map(entry => entry.pathDigest)),
      markers: normalizedMarkers,
      basePath: initData.pullRequest.pathName,
      pendingReview,
      pullRequest: initData.pullRequest,
      baseRepository: initData.repository,
      selectedPathDigest: parsePathDigestWithoutLineNumbers(getSelectedFullDiffHash()),
      initialActiveBatch: loadedBatch.length > 0 ? loadedBatch : undefined,
      showChangeGroupPreference: initData.user.viewSettings.showChangeGroups,
      configOpts,
      viewSettings,
    })
  }

  // We disable ESlint refs here as we need our Zustand stores to be a stable reference
  // Using a useState to store our Zustand store will cause unnecessary re-renders and downstream performance issues
  // eslint-disable-next-line react-hooks/refs
  return <PullRequestStoreContext value={storeRef.current}>{children}</PullRequestStoreContext>
}

const createPullRequestStore = (initState: InitialState) => {
  return createStore<State>()(
    devtools(
      (...a) => ({
        ...createChangeGroupGenerationSlice(initState)(...a),
        ...createCodeownersSlice()(...a),
        ...createComparisonInfoSlice(initState)(...a),
        ...createConfigOptsSlice(initState)(...a),
        ...createCurrentUserSlice(initState)(...a),
        ...createDiffFilterSlice()(...a),
        ...createDiffSummariesSlice(initState)(...a),
        ...createMarkersSlice(initState)(...a),
        ...createProgressiveDiffsSlice(initState)(...a),
        ...createFilesCollapsedStatusSlice(initState)(...a),
        ...createPendingReviewSlice(initState)(...a),
        ...createDiffEntriesSlice(initState)(...a),
        ...createMarkerAndDiffNavigationSlice(initState)(...a),
        ...createSettingsSlice(initState)(...a),
        ...createPullRequestSlice(initState)(...a),
        ...createRepositorySlice(initState)(...a),
        ...createSuggestionBatchSlice(initState)(...a),
        ...createViewSettingsSlice(initState)(...a),
      }),
      {name: 'pull-request-store'},
    ),
  )
}

function populateTanstackDiffEntryCache(
  startOid: string | null | undefined,
  endOid: string | null | undefined,
  basePath: string,
  diffEntries: DiffEntry[],
  comparison: {
    baseOid: string
    headOid: string
  },
  hideWhitespace: boolean,
): void {
  const queryClient = getQueryClient()
  const queryKey = diffEntriesKey({
    basePath,
    effectiveRange: {
      baseOid: startOid ?? comparison.baseOid,
      headOid: endOid ?? comparison.headOid,
    },
    hideWhitespace,
  })
  queryClient.setQueryData(queryKey, diffEntries)
}

try{ PullRequestStoreContext.displayName ||= 'PullRequestStoreContext' } catch {}
try{ PullRequestStoreProvider.displayName ||= 'PullRequestStoreProvider' } catch {}